import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import defaultCteLogo from '../assets/acuity-cte-logo.png';

const LEGACY_LOGO_URL = 'https://acuitycte-files.s3.us-west-2.amazonaws.com/public/acuity-tech-logo.png';

const isGlobalAccessCard = (card) => !card || card.global === true || card.school_id == null;

const resolveLogoUrl = (card) => {
  if (isGlobalAccessCard(card)) return defaultCteLogo;
  const schoolLogo = card?.school?.logo_url;
  if (schoolLogo && schoolLogo !== LEGACY_LOGO_URL) return schoolLogo;
  return defaultCteLogo;
};

const ICONS = {
  'My Hours': '⏱️',
  'Register for Events': '📅',
  'FE Event Sign Up': '📅',
  'FE Events (Student View)': '📅',
  'Projects Directory': '📂',
  'Coursework': '📘',
  'Request FE Hours': '📝',
  'My Account': '👤',
  'User Management': '👥',
  'Schedule Builder': '🗓️',
  'Community': '🏘️',
  'My Events': '🎪',
  'Create Event': '➕',
  'Event Attendance': '✅',
  'Project Builder': '🛠️',
  'Assignment Builder': '📚',
  'Course Builder': '🏗️',
  'Course Enroll Manager': '👥',
  'My Courses': '🎓',
  'LMS Roster & Progress': '🏫',
  'Preview Courses': '🎬',
  'Waiver Forms': '📄',
  'My Directory': '📝',
  'Admin Directory': '📋',
  'Newsletter': '📰',
  'View Posts': '📬',
  'Register Students for Events': '🗓',
  'Instructor Concentrations': '🎯',
  'Taxonomy Management': '🏷️',
  'Assignment Submissions Auditor': '🔍',
  'Page Access Manager': '🔐',
  'CSV Import': '📊',
  'Users - CSV Import': '⬇️',
  'Analytics': '📈',
  'Student Hours': '📊',
  'Home': '🏠',
  'Logout': '🚪',
  'Login': '🔑'
};

const Header = ({ userData, setUserData, navQuickLinks = [], navQuickLinksReady = true }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const defaultAccessCard = userData?.accesscards?.find(
    ac => ac.accesscard_id === userData.default_accesscard_id
  );
  const role = defaultAccessCard?.role;

  const handleLogout = async () => {
    // Track logout before clearing data
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post('/api/track-session', {
          sessionType: 'logout',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (trackingError) {
      console.warn('Error tracking logout:', trackingError);
      // Don't fail logout if tracking fails
    }
    
    localStorage.removeItem('token');
    setUserData(null); // Clear userData
    navigate('/');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClick = (path) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  // Handle clicking outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const fullName = userData ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim() : '';

  const menuItems = navQuickLinksReady ? navQuickLinks : [];

  return (
    <header style={styles.header}>
      <div style={styles.leftSection}>
        <Link to="/" style={styles.logoLink}>
          <img 
            src={resolveLogoUrl(defaultAccessCard)}
            alt="Acuity Career Technical Education" 
            style={styles.logo}
          />
        </Link>
        <div style={styles.menuContainer} ref={menuRef}>
          <button onClick={toggleMenu} style={styles.menuButton}>
            Menu
          </button>
          {isMenuOpen && (
            <div style={styles.dropdownMenu}>
              <button 
                onClick={() => handleMenuClick('/')} 
                style={styles.menuItem}
              >
                <span style={styles.menuIcon}>{ICONS['Home']}</span>
                <span>Home</span>
              </button>
              {menuItems.map((item, index) => (
                <button 
                  key={`${item.path}-${item.label}-${index}`}
                  onClick={() => handleMenuClick(item.path)} 
                  style={styles.menuItem}
                >
                  <span style={styles.menuIcon}>{item.icon || ICONS[item.label] || '🔗'}</span>
                  <span>{item.label}</span>
                </button>
              ))}
              <div style={styles.menuDivider}></div>
              {userData ? (
                <button 
                  onClick={handleLogout} 
                  style={styles.menuItem}
                >
                  <span style={styles.menuIcon}>{ICONS['Logout']}</span>
                  <span>Logout</span>
                </button>
              ) : (
                <button 
                  onClick={() => handleMenuClick('/login')} 
                  style={styles.menuItem}
                >
                  <span style={styles.menuIcon}>{ICONS['Login']}</span>
                  <span>Login</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <div style={styles.userInfo}>
        <span 
          style={styles.welcome}
          onClick={() => navigate('/dashboard')}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f0f8ff';
            e.target.style.color = '#0f3a6b';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = '#1D4E89';
          }}
        >
          Hello, {fullName || 'Guest'} ({role || 'Guest'})
        </span>
      </div>
    </header>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 2rem 0',
    backgroundColor: 'white',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 1000
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem'
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center'
  },
  logo: {
    height: '100px',
    width: 'auto',
    objectFit: 'contain'
  },
  menuContainer: {
    position: 'relative'
  },
  menuButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1D4E89',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.3s ease'
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    minWidth: '300px',
    zIndex: 1001,
    marginTop: '0.5rem',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '0.75rem 1rem',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#333',
    transition: 'all 0.3s ease',
    gap: '0.75rem'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  welcome: {
    color: '#1D4E89',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  menuDivider: {
    height: '1px',
    backgroundColor: '#ddd',
    margin: '0.5rem 0'
  },
  menuIcon: {
    fontSize: '1.1rem',
    minWidth: '1.5rem',
    textAlign: 'center'
  }
};

export default Header; 